import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDateTime, fmtNum, parseIso, todayStartZurich } from "../lib/format";
import { ShoppingCart, Plus, Trash2, Search, X, Calendar, TrendingUp, TrendingDown, Minus, AlertCircle, RefreshCw, Zap, Activity } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { useConfirm } from "../components/ConfirmDialog";
import { NayaxFreshness } from "../components/sales/NayaxFreshness";
import { toast } from "sonner";

const WINDOWS = [
  // Phase 87bk — On vire le « 24h glissant » (sans intérêt opérationnel).
  // On affiche Aujourd'hui (calendaire UTC, depuis 00:00) en premier — pour suivre
  // les ventes en direct sans devoir ouvrir Moma. Le scheduler synchro Nayax
  // toutes les 15 min, donc max 15 min de décalage.
  {
    key: "today",
    label: "Aujourd'hui",
    title: "Ventes du jour calendaire UTC (depuis 00:00). Rafraîchi à chaque sync Nayax (scheduler 15 min). Pour comparer avec Moma sur une date passée, utilise Diagnostic → Audit ventes par date.",
  },
  {
    key: "yesterday",
    label: "Hier (UTC)",
    title: "Jour calendaire UTC complet (00:00 → 23:59 UTC). Correspond à ce que tu vois dans les rapports Moma de la veille.",
  },
  { key: "7d", label: "7 jours" },
  { key: "30d", label: "30 jours" },
  { key: "90d", label: "90 jours" },
  { key: "365d", label: "1 an" },
];

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [lots, setLots] = useState([]);
  const [velocity, setVelocity] = useState({ totals: {}, by_distributor: {} });
  const [expiredStats, setExpiredStats] = useState({ totals: {}, by_distributor: {} });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ distributor_id: "", product_id: "", lot_id: "auto", quantity: 1, unit_price: "", payment_method: "card", sold_at: "", notes: "" });
  const [filterDist, setFilterDist] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadErrors, setLoadErrors] = useState([]); // endpoints qui ont échoué
  const [syncingNayax, setSyncingNayax] = useState(false);
  const [nayaxStatus, setNayaxStatus] = useState(null);  // Phase 87bk — indicateur sync
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const confirm = useConfirm();

  // Phase 87be — Chargement RÉSILIENT : chaque appel est indépendant.
  // Si un endpoint tombe (ex: /api/sales/velocity timeout en prod sur grosse volumétrie),
  // les autres données s'affichent quand même. Un toast warning liste les échecs.
  // Améliorations 87be :
  //  - Retry silencieux sur Network Error (1 tentative supplémentaire après 800 ms)
  //    pour éliminer les faux positifs après une sync Nayax qui sature la connexion.
  //  - Le bandeau ne s'affiche QUE si la 2e tentative échoue aussi.
  const fetchWithRetry = async (url, maxAttempts = 2) => {
    let lastErr;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await api.get(url);
      } catch (err) {
        lastErr = err;
        // Retry uniquement si pas de réponse (Network Error / timeout côté client).
        // Si le serveur a répondu avec 4xx/5xx, on ne retry pas.
        const noResponse = !err?.response;
        if (!noResponse) break;
        if (attempt < maxAttempts) {
          await new Promise((r) => setTimeout(r, 800));
        }
      }
    }
    throw lastErr;
  };

  const load = async () => {
    setLoading(true);
    const sources = [
      { key: "sales",       label: "Ventes",        url: "/sales",                set: setSales,         empty: [] },
      { key: "products",    label: "Produits",      url: "/products",             set: setProducts,      empty: [] },
      { key: "distributors",label: "Distributeurs", url: "/distributors",         set: setDistributors,  empty: [] },
      { key: "lots",        label: "Lots",          url: "/lots?active_only=true&slim=true", set: setLots,          empty: [] },
      { key: "velocity",    label: "Vélocité",      url: "/sales/velocity",       set: setVelocity,      empty: { totals: {}, by_distributor: {} } },
      { key: "expired",     label: "Pertes DLC",    url: "/expired-stock/stats",  set: setExpiredStats,  empty: { totals: {}, by_distributor: {} } },
      { key: "nayax",       label: "Statut Nayax",  url: "/nayax/status",         set: setNayaxStatus,   empty: null },
    ];
    const results = await Promise.allSettled(sources.map((s) => fetchWithRetry(s.url)));
    const failed = [];
    results.forEach((r, i) => {
      const src = sources[i];
      if (r.status === "fulfilled") {
        src.set(r.value.data ?? src.empty);
      } else {
        src.set(src.empty);
        failed.push({ label: src.label, url: src.url, err: r.reason?.response?.status || r.reason?.message || "?" });
        console.warn(`[Ventes] échec /api${src.url}`, r.reason);
      }
    });
    setLoadErrors(failed);
    setLoading(false);
    setLastRefreshAt(new Date());
    if (failed.length > 0) {
      toast.warning(
        `${failed.length} flux n'a pas pu être chargé : ${failed.map((f) => f.label).join(", ")}. Les données disponibles sont affichées.`,
        { duration: 8000 }
      );
    }
  };

  // Phase 87bk — Refresh silencieux des données opérationnelles (velocity + nayax status)
  // sans réafficher le toast d'erreur ni passer en mode loading. Pour suivre les ventes
  // "just-in-time" toutes les 60s sans avoir à F5.
  const silentRefresh = async () => {
    const sources = [
      { url: "/sales/velocity", set: setVelocity },
      { url: "/expired-stock/stats", set: setExpiredStats },
      { url: "/nayax/status", set: setNayaxStatus },
    ];
    const results = await Promise.allSettled(sources.map((s) => api.get(s.url)));
    results.forEach((r, i) => {
      if (r.status === "fulfilled") sources[i].set(r.value.data);
    });
    setLastRefreshAt(new Date());
  };

  useEffect(() => { load(); }, []);

  // Phase 87bk — Auto-refresh silencieux toutes les 60s (juste les KPI critiques)
  useEffect(() => {
    const interval = setInterval(() => {
      silentRefresh();
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  const productMap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const distMap = useMemo(() => Object.fromEntries(distributors.map((d) => [d.id, d])), [distributors]);

  // Lots disponibles dans le dépôt sélectionné pour le produit choisi
  const eligibleLots = useMemo(() => {
    if (!form.distributor_id || !form.product_id) return [];
    return lots
      .filter((x) => x.distributor_id === form.distributor_id && x.product_id === form.product_id && (x.quantity || 0) > 0)
      .sort((a, b) => (a.dlc || "").localeCompare(b.dlc || ""));
  }, [lots, form.distributor_id, form.product_id]);

  // Produits ayant du stock dans le dépôt sélectionné
  const eligibleProducts = useMemo(() => {
    if (!form.distributor_id) return products;
    const productIds = new Set(
      lots.filter((x) => x.distributor_id === form.distributor_id && (x.quantity || 0) > 0).map((x) => x.product_id)
    );
    return products.filter((p) => productIds.has(p.id));
  }, [lots, form.distributor_id, products]);

  // Pré-remplit le prix unitaire selon le type de dépôt + niveau tarifaire client + produit
  useEffect(() => {
    if (!form.distributor_id || !form.product_id) return;
    const dist = distributors.find((d) => d.id === form.distributor_id);
    const prod = products.find((p) => p.id === form.product_id);
    if (!dist || !prod) return;
    let suggested = null;
    if (dist.type === "CLIENT") {
      const tier = dist.price_tier || "direct";
      if (tier === "distributor") suggested = prod.price_distributor ?? prod.price_direct ?? prod.price_online;
      else if (tier === "online") suggested = prod.price_online ?? prod.price_direct ?? prod.price_distributor;
      else suggested = prod.price_direct ?? prod.price_distributor ?? prod.price_online;
    } else if (dist.type === "AUTOMATIQUE") {
      suggested = prod.price_direct ?? prod.price_distributor ?? prod.price_online;
    }
    if (suggested != null && suggested !== "") {
      setForm((f) => (f.unit_price === "" ? { ...f, unit_price: String(suggested) } : f));
    }
  }, [form.distributor_id, form.product_id]);

  const reset = () => setForm({ distributor_id: "", product_id: "", lot_id: "auto", quantity: 1, unit_price: "", payment_method: "card", sold_at: "", notes: "" });

  const save = async () => {
    if (!form.distributor_id) { toast.error("Sélectionnez un dépôt"); return; }
    if (!form.product_id) { toast.error("Sélectionnez un produit"); return; }
    if (!form.quantity || Number(form.quantity) <= 0) { toast.error("Quantité invalide"); return; }
    try {
      await api.post("/sales", {
        distributor_id: form.distributor_id,
        product_id: form.product_id,
        lot_id: form.lot_id === "auto" ? null : form.lot_id,
        quantity: Number(form.quantity),
        unit_price: form.unit_price ? Number(form.unit_price) : null,
        payment_method: form.payment_method || null,
        sold_at: form.sold_at || null,
        notes: form.notes,
      });
      toast.success("Vente enregistrée");
      setOpen(false);
      reset();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la vente");
    }
  };

  const cancel = async (sale) => {
    const ok = await confirm({
      title: "Annuler cette vente ?",
      description: `La quantité (${fmtNum(sale.quantity)}) sera restaurée dans le lot ${sale.lot_number || ""} et le mouvement de sortie associé sera supprimé.`,
      confirmText: "Annuler la vente",
      cancelText: "Garder",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/sales/${sale.id}`);
      toast.success("Vente annulée");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  // Phase 87ay — Force sync Nayax depuis le module Ventes (debug rapide
  // sans aller dans Distributeurs). Déclenche /api/nayax/sync-all puis reload.
  const syncNayaxNow = async () => {
    setSyncingNayax(true);
    try {
      const { data } = await api.post("/nayax/sync-all");
      const total = (data?.results || []).reduce((acc, r) => acc + (r.sales_created || 0), 0);
      const skipped = (data?.results || []).reduce((acc, r) => acc + (r.skipped_duplicates || 0) + (r.skipped_before_cutoff || 0), 0);
      const failed = (data?.results || []).reduce((acc, r) => acc + (r.failed || 0), 0);
      toast.success(`Sync Nayax terminée : ${total} nouvelles ventes, ${skipped} déjà vues, ${failed} en erreur`);
      // Phase 87be — Petite pause pour laisser le backend finir les writes mongo
      // avant de relancer les 6 GET en parallèle. Évite les network errors
      // transients (le browser cancel parfois quand une connexion vient d'être longue).
      await new Promise((r) => setTimeout(r, 600));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync Nayax échouée");
    } finally {
      setSyncingNayax(false);
    }
  };

  const filtered = sales.filter((s) => {
    if (filterDist !== "all" && s.distributor_id !== filterDist) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const p = productMap[s.product_id];
    return (s.lot_number || "").toLowerCase().includes(q) ||
           (p?.name || "").toLowerCase().includes(q) ||
           (p?.sku || "").toLowerCase().includes(q);
  });

  // Phase 87ba — compteur Nayax aujourd'hui (heure suisse, robuste TZ).
  // Avant: `new Date(s.sold_at)` parsait les dates Nayax (sans Z) comme heures
  // locales → décalage 1-2h → certaines ventes "ratées" du jour.
  // Maintenant: parseIso() force UTC quand pas de tz, et todayStartZurich()
  // renvoie minuit en Suisse — peu importe le fuseau du navigateur.
  const nayaxCounts = useMemo(() => {
    const todayStart = todayStartZurich();
    const last24h = new Date(Date.now() - 24 * 3600 * 1000);
    let today = 0;
    let last24 = 0;
    let totalNayax = 0;
    for (const s of sales) {
      if (s.source !== "NAYAX") continue;
      totalNayax += 1;
      const dt = parseIso(s.sold_at) || parseIso(s.created_at);
      if (dt) {
        if (dt >= todayStart) today += 1;
        if (dt >= last24h) last24 += 1;
      }
    }
    return { today, last24, totalNayax };
  }, [sales]);

  const trendIcon = (curr, prev) => {
    if (prev === 0 || prev === undefined) return <Minus className="w-3 h-3 text-[#A3A6A3]" />;
    const diff = curr - prev;
    if (Math.abs(diff) < 0.01) return <Minus className="w-3 h-3 text-[#A3A6A3]" />;
    return diff > 0
      ? <TrendingUp className="w-3 h-3 text-[#4F8A6F]" />
      : <TrendingDown className="w-3 h-3 text-[#C05C47]" />;
  };

  return (
    <div className="space-y-6" data-testid="sales-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading text-[#1A1C1A]">Ventes</h1>
          <p className="text-sm text-[#575A57]">
            Suivi des ventes par distributeur · <span className="text-[#5847A6]">sync Nayax automatique toutes les 15 min</span>
          </p>
          {/* Compteurs Nayax — visibles immédiatement pour debug rapide */}
          <div className="flex items-center gap-2 mt-2 text-xs flex-wrap" data-testid="nayax-counts">
            <Badge className="bg-[#E8E4F8] text-[#5847A6] border-[#C8C0E8]" data-testid="nayax-today">
              <Zap className="w-3 h-3 mr-1" /> {nayaxCounts.today} Nayax aujourd&apos;hui
            </Badge>
            <Badge variant="outline" data-testid="nayax-24h">
              {nayaxCounts.last24} en 24 h
            </Badge>
            <Badge variant="outline" data-testid="nayax-total">
              {nayaxCounts.totalNayax} Nayax total
            </Badge>
            <Badge variant="outline" data-testid="sales-manual-total">
              {sales.filter((s) => s.source !== "NAYAX").length} manuelles
            </Badge>
            {/* Phase 87bk — Indicateur de fraîcheur des données (auto-refresh 60s + sync 15 min) */}
            <NayaxFreshness status={nayaxStatus} lastRefreshAt={lastRefreshAt} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            asChild
            data-testid="open-diagnostic-btn"
            title="Voir les transactions Nayax brutes et leur statut"
          >
            <Link to="/nayax/diagnostic">
              <Activity className="w-4 h-4 mr-2" /> Diagnostic
            </Link>
          </Button>
          <Button
            variant="outline"
            onClick={syncNayaxNow}
            disabled={syncingNayax || loading}
            data-testid="force-nayax-sync-btn"
            title="Force la synchro Nayax maintenant (debug)"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncingNayax ? "animate-spin" : ""}`} />
            {syncingNayax ? "Sync en cours…" : "Forcer sync Nayax"}
          </Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-sale-btn">
                <Plus className="w-4 h-4 mr-2" /> Nouvelle vente
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-heading">Enregistrer une vente</DialogTitle>
              <DialogDescription>Le stock du lot sélectionné (FIFO par DLC par défaut) sera décrémenté et un mouvement de sortie sera créé automatiquement.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="mb-1.5 block">Dépôt *</Label>
                <Select value={form.distributor_id} onValueChange={(v) => setForm({ ...form, distributor_id: v, product_id: "", lot_id: "auto" })}>
                  <SelectTrigger data-testid="sale-distributor"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                  <SelectContent>
                    {distributors.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}{d.code ? ` (${d.code})` : ""}{d.type === "CLIENT" ? " · Client" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">Produit *</Label>
                <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v, lot_id: "auto" })} disabled={!form.distributor_id}>
                  <SelectTrigger data-testid="sale-product"><SelectValue placeholder={form.distributor_id ? "Sélectionner…" : "Sélectionnez d'abord un dépôt"} /></SelectTrigger>
                  <SelectContent>
                    {eligibleProducts.length === 0 ? (
                      <div className="px-2 py-3 text-xs text-[#575A57]">Aucun produit en stock dans ce dépôt</div>
                    ) : eligibleProducts.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} {p.sku ? `(${p.sku})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {form.product_id && eligibleLots.length > 0 && (
                <div>
                  <Label className="mb-1.5 block">Lot</Label>
                  <Select value={form.lot_id} onValueChange={(v) => setForm({ ...form, lot_id: v })}>
                    <SelectTrigger data-testid="sale-lot"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">FIFO automatique (DLC la plus proche)</SelectItem>
                      {eligibleLots.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.lot_number} · qté {fmtNum(l.quantity)} · DLC {l.dlc || "—"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1.5 block">Quantité *</Label>
                  <Input type="number" step="1" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="sale-quantity" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Prix unitaire (CHF)</Label>
                  <Input type="number" step="0.10" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} placeholder="Optionnel" data-testid="sale-price" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1.5 block">Mode de paiement</Label>
                  <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                    <SelectTrigger data-testid="sale-payment"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="card">Carte</SelectItem>
                      <SelectItem value="cash">Espèces</SelectItem>
                      <SelectItem value="app">Application</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block">Date de vente</Label>
                  <Input type="datetime-local" value={form.sold_at} onChange={(e) => setForm({ ...form, sold_at: e.target.value })} data-testid="sale-date" />
                </div>
              </div>
              <div>
                <Label className="mb-1.5 block">Notes</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optionnel" data-testid="sale-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={save} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-sale-btn">Enregistrer la vente</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Bandeau d'erreur de chargement — Phase 87ay */}
      {loadErrors.length > 0 && (
        <div className="bg-[#FBEEEA] border border-[#E8B0A0] rounded p-3 text-sm flex items-start gap-2" data-testid="load-errors-banner">
          <AlertCircle className="w-4 h-4 text-[#A04E2C] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-[#A04E2C] font-medium mb-0.5">Chargement partiel</div>
            <div className="text-xs text-[#A04E2C]">
              Endpoints en échec : {loadErrors.map((e) => `${e.label} (${e.err})`).join(" · ")}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="retry-load-btn">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Recharger
          </Button>
        </div>
      )}

      {/* Stat cards — Vélocité ventes vs Pertes DLC */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-[#E5E5E0]" data-testid="sales-velocity-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-[#1F4E3D]" />
                <span className="text-sm font-medium text-[#1A1C1A]">Vélocité ventes (toutes machines)</span>
              </div>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {WINDOWS.map((w) => (
                <div
                  key={w.key}
                  className={`rounded p-2 text-center ${w.key === "today" ? "bg-[#E8E4F8] border-2 border-[#5847A6] shadow-sm" : w.key === "yesterday" ? "bg-[#EAF4EE] border border-[#C1DCC9]" : "bg-[#F9F9F6]"}`}
                  title={w.title || ""}
                  data-testid={`velocity-card-${w.key}`}
                >
                  <div className="text-[10px] uppercase tracking-wide text-[#A3A6A3] flex items-center justify-center gap-1">
                    {w.label}
                    {w.title && <span className="text-[9px] text-[#5847A6]" aria-hidden>ⓘ</span>}
                  </div>
                  <div className={`font-mono ${w.key === "today" ? "text-2xl font-bold text-[#5847A6]" : w.key === "yesterday" ? "text-lg text-[#1F4E3D]" : "text-lg text-[#1A1C1A]"}`}>{fmtNum(velocity?.totals?.[w.key] || 0)}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E5E0]" data-testid="dlc-loss-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-[#C05C47]" />
                <span className="text-sm font-medium text-[#1A1C1A]">Pertes DLC (toutes machines)</span>
              </div>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
              {WINDOWS.filter((w) => w.key !== "yesterday" && w.key !== "today").map((w) => (
                <div key={w.key} className="bg-[#FCEEEA] rounded p-2 text-center" title={w.title || ""}>
                  <div className="text-[10px] uppercase tracking-wide text-[#A3A6A3] flex items-center justify-center gap-1">
                    {w.label}
                    {w.title && <span className="text-[9px] text-[#5847A6]" aria-hidden>ⓘ</span>}
                  </div>
                  <div className="font-mono text-lg text-[#C05C47]">{fmtNum(expiredStats?.totals?.[w.key] || 0)}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table par distributeur */}
      <Card className="border-[#E5E5E0]" data-testid="velocity-by-distributor">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-[#E5E5E0] flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#1F4E3D]" />
            <span className="text-sm font-medium text-[#1A1C1A]">Ventes vs Pertes DLC par distributeur</span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Distributeur</TableHead>
                {WINDOWS.map((w) => (
                  <TableHead
                    key={w.key}
                    className={`text-right ${w.key === "today" ? "bg-[#E8E4F8]/60 text-[#5847A6] font-bold" : w.key === "yesterday" ? "bg-[#EAF4EE]/40 text-[#1F4E3D]" : ""}`}
                    title={w.title || ""}
                  >
                    {w.label}
                    {w.title && <span className="text-[9px] text-[#5847A6] ml-1" aria-hidden>ⓘ</span>}
                  </TableHead>
                ))}
                <TableHead className="text-right">Pertes 30j</TableHead>
                <TableHead className="text-right">Ratio pertes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {distributors.length === 0 ? (
                <TableRow><TableCell colSpan={WINDOWS.length + 3} className="text-center text-[#A3A6A3] py-8">Aucun distributeur</TableCell></TableRow>
              ) : distributors.map((d) => {
                const v = velocity?.by_distributor?.[d.id] || {};
                const x = expiredStats?.by_distributor?.[d.id] || {};
                const sales30 = v["30d"] || 0;
                const loss30 = x["30d"] || 0;
                const total30 = sales30 + loss30;
                const ratio = total30 > 0 ? (loss30 / total30) * 100 : 0;
                return (
                  <TableRow key={d.id} data-testid={`velocity-row-${d.id}`}>
                    <TableCell>
                      <div className="font-medium text-[#1A1C1A]">{d.name}</div>
                      {d.type === "CLIENT" && <span className="text-[10px] text-[#7B5BC9]">Client</span>}
                    </TableCell>
                    {WINDOWS.map((w) => (
                      <TableCell
                        key={w.key}
                        className={`text-right font-mono ${w.key === "today" ? "bg-[#E8E4F8]/60 text-[#5847A6] font-bold text-base" : w.key === "yesterday" ? "bg-[#EAF4EE]/40 text-[#1F4E3D] font-semibold" : ""}`}
                      >
                        {fmtNum(v[w.key] || 0)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right font-mono text-[#C05C47]">{fmtNum(loss30)}</TableCell>
                    <TableCell className="text-right">
                      {total30 > 0 ? (
                        <Badge className={ratio > 20 ? "bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]" : ratio > 10 ? "bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]" : "bg-[#EAF4EE] text-[#4F8A6F] border-[#C1DCC9]"}>
                          {ratio.toFixed(1)}%
                        </Badge>
                      ) : <span className="text-[#A3A6A3]">—</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Filtres et historique */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={filterDist} onValueChange={setFilterDist}>
          <SelectTrigger className="w-[240px]" data-testid="sales-distfilter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les distributeurs</SelectItem>
            {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-[#A3A6A3] absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            type="text"
            placeholder="Rechercher lot, produit, SKU…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 pr-8"
            data-testid="sales-search"
          />
          {search && (
            <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A3A6A3] hover:text-[#575A57]">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <Card className="border-[#E5E5E0]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Distributeur</TableHead>
                <TableHead>Produit</TableHead>
                <TableHead>Lot</TableHead>
                <TableHead className="text-right">Quantité</TableHead>
                <TableHead className="text-right">Prix unit. (CHF)</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-[#A3A6A3] py-12">
                    <Calendar className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    Aucune vente enregistrée. Cliquez sur « Nouvelle vente » pour commencer.
                  </TableCell>
                </TableRow>
              ) : filtered.map((s) => {
                const p = productMap[s.product_id];
                const d = distMap[s.distributor_id];
                return (
                  <TableRow key={s.id} data-testid={`sale-row-${s.id}`}>
                    <TableCell className="font-mono text-xs text-[#575A57]">{fmtDateTime(s.sold_at)}</TableCell>
                    <TableCell>{d?.name || "—"}</TableCell>
                    <TableCell>
                      <div>{p?.name || "—"}</div>
                      <div className="text-[11px] text-[#A3A6A3]">{p?.sku}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{s.lot_number || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{fmtNum(s.quantity)}</TableCell>
                    <TableCell className="text-right font-mono">{s.unit_price ? Number(s.unit_price).toFixed(2) : "—"}</TableCell>
                    <TableCell>
                      {s.source === "NAYAX"
                        ? <Badge className="bg-[#E8E4F8] text-[#5847A6] border-[#C8C0E8]">Nayax</Badge>
                        : <Badge variant="outline">Manuel</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => cancel(s)} title="Annuler la vente" data-testid={`cancel-sale-${s.id}`}>
                        <Trash2 className="w-4 h-4 text-[#C05C47]" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
